export interface TocEntry {
  id: string;
  text: string;
  level: number;
}

/**
 * The headings that are on the page: every rendered h1 to h4 that has an id,
 * in page order, with its text as the reader sees it.
 *
 * Read from the rendered article, not from the markdown source. A second
 * parser beside the renderer's disagreed with it: a `# comment` in a code
 * block became an entry with nothing to scroll to, and a heading kept its raw
 * markdown in the list. A heading with no id cannot be linked to, so it is
 * left out rather than listed as a link that does nothing.
 */
export function readHeadings(article: ParentNode | null): TocEntry[] {
  if (!article) return [];
  return Array.from(article.querySelectorAll<HTMLElement>('h1, h2, h3, h4'))
    .map((heading) => ({
      id: heading.id,
      text: (heading.textContent ?? '').trim(),
      level: Number(heading.tagName.slice(1)),
    }))
    .filter((entry) => entry.id !== '' && entry.text !== '');
}

/**
 * The "On this page" list. Its position (pinned beside the article, never
 * taller than the reader) is CSS: see .docs-toc in docs-shared.css.
 */
export function TableOfContents({ entries }: { entries: TocEntry[] }) {
  if (entries.length < 3) return null;

  function handleClick(e: React.MouseEvent, id: string) {
    // <main> is a custom overflow-y:auto scroll container, so native
    // href="#anchor" scrolls the window (no-op here). Use scrollIntoView
    // instead — it targets the nearest scrolling ancestor, which is <main>.
    const target = document.getElementById(id);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  return (
    <nav className="docs-toc" aria-label="On this page">
      <p className="docs-toc-title">On this page</p>
      <ul className="docs-toc-list">
        {entries.map((entry, index) => (
          // The renderer makes ids unique, but a document may write its own
          // <h2 id="…"> twice: the position keeps the key unique regardless.
          <li key={`${index}:${entry.id}`}>
            <a
              href={`#${entry.id}`}
              className="docs-toc-link"
              data-level={entry.level}
              onClick={(e) => handleClick(e, entry.id)}
            >
              {entry.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
